import React, { useEffect, useRef, useState } from "react";
import { ShieldCheckIcon, CloudArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getSupabaseClient } from "../lib/supabase";

const ACCEPTED = ["application/pdf", "image/png", "image/jpeg"];
const MAX_MB = 10;
const BUCKET = "background-checks";

type BgCheck = {
  id: string;
  status: string;
  original_filename: string;
  submitted_at: string | null;
  created_at: string | null;
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  uploaded: { label: "Under Review", color: "#d97706", bg: "#FEF3C7" },
  pending: { label: "Under Review", color: "#d97706", bg: "#FEF3C7" },
  approved: { label: "Verified", color: "#059669", bg: "#D1FAE5" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#FEE2E2" },
};

export default function BackgroundCheckCard() {
  const [bgCheck, setBgCheck] = useState<BgCheck | null>(null);
  const [adminVerified, setAdminVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const loadBgCheck = async () => {
    try {
      const sb = getSupabaseClient();
      const { data: { user } } = await sb.auth.getUser();
      if (!user) return;

      const [checkResult, profileResult] = await Promise.all([
        sb
          .from("guide_background_checks")
          .select("id, status, original_filename, submitted_at, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        sb
          .from("profiles")
          .select("backgroundChecked")
          .eq("id", user.id)
          .maybeSingle(),
      ]);

      setBgCheck(checkResult.data ?? null);
      setAdminVerified(profileResult.data?.backgroundChecked === true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBgCheck(); }, []);

  const effectiveStatus = adminVerified ? "approved" : (bgCheck?.status ?? "");
  const statusInfo = STATUS_CONFIG[effectiveStatus] ?? STATUS_CONFIG.pending;

  if (loading) {
    return (
      <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
        <div className="animate-pulse h-6 w-48 bg-[#2C2731]/10 rounded" />
      </section>
    );
  }

  return (
    <>
      <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="h-6 w-6 text-[#2C2731] mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-medium text-[#2C2731]">Background Check</h2>
              <p className="text-sm text-[#2C2731]/70 mt-0.5">
                {adminVerified
                  ? "Your background check has been verified."
                  : bgCheck
                    ? "Your background check has been submitted."
                    : "Upload a criminal background check to earn a verified badge on your profile."}
              </p>
            </div>
          </div>

          {bgCheck || adminVerified ? (
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap"
              style={{ color: statusInfo.color, backgroundColor: statusInfo.bg }}
            >
              {statusInfo.label}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#2C2731] bg-[#2C2731] px-4 py-1.5 text-sm font-medium text-[#FFFCEF] transition hover:bg-[#241F2A] whitespace-nowrap"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              Upload
            </button>
          )}
        </div>

        {bgCheck && (
          <div className="mt-4 rounded-lg bg-[#2C2731]/5 px-4 py-3 text-sm text-[#2C2731]/80">
            <div className="flex justify-between">
              <span>File</span>
              <span className="font-medium text-[#2C2731]">{bgCheck.original_filename}</span>
            </div>
            {(bgCheck.submitted_at || bgCheck.created_at) && (
              <div className="flex justify-between mt-1">
                <span>Submitted</span>
                <span className="font-medium text-[#2C2731]">
                  {new Date(bgCheck.submitted_at ?? bgCheck.created_at!).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {modalOpen && (
        <BackgroundCheckModal
          onClose={() => setModalOpen(false)}
          onUploaded={() => {
            setModalOpen(false);
            loadBgCheck();
          }}
        />
      )}
    </>
  );
}

/* ─── Upload Modal ────────────────────────────────────────────────── */

function BackgroundCheckModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickFile = () => inputRef.current?.click();

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);

    if (!ACCEPTED.includes(f.type)) {
      setError("Only PDF, JPG, or PNG files are accepted.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError("File must be 10 MB or smaller.");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);

    try {
      const sb = getSupabaseClient();
      const { data: { user } } = await sb.auth.getUser();
      if (!user) throw new Error("Not logged in.");

      const safeName = file.name.replace(/[^\w.\-]+/g, "_");
      const objectKey = `guides/${user.id}/${Date.now()}-${safeName}`;

      const { error: upErr } = await sb.storage.from(BUCKET).upload(objectKey, file, { upsert: true });
      if (upErr) throw upErr;

      const { error: insErr } = await sb.from("guide_background_checks").insert({
        user_id: user.id,
        storage_object_key: objectKey,
        storage_bucket: BUCKET,
        original_filename: file.name,
        status: "uploaded",
        file_path: objectKey,
      });
      if (insErr) throw insErr;

      onUploaded();
    } catch (e: any) {
      setError(e?.message ?? "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-medium text-[#2C2731]">Upload Background Check</h2>
            <p className="text-sm text-[#2C2731]/70 mt-1">
              Upload a criminal background check to earn a verified badge.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-[#2C2731]/60 hover:bg-[#2C2731]/10 transition"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Drop zone */}
        <div
          className="border-2 border-dashed border-[#2C2731]/30 rounded-xl py-10 text-center cursor-pointer hover:bg-[#2C2731]/5 transition-colors mb-4"
          onClick={pickFile}
        >
          <CloudArrowUpIcon className="h-10 w-10 mx-auto mb-3 text-[#2C2731]/40" />
          {file ? (
            <p className="text-sm font-medium text-[#2C2731]">{file.name}</p>
          ) : (
            <>
              <p className="text-sm text-[#2C2731]/70">
                Drop your file here, or{" "}
                <span className="text-[#2C2731] underline font-medium">browse</span>
              </p>
            </>
          )}
          <p className="text-xs mt-1.5 text-[#2C2731]/50">PDF, JPG, PNG — up to 10 MB</p>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={onFile}
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] hover:bg-[#F3EBCD] transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || busy}
            className={`rounded-full px-5 py-2 text-sm font-medium text-[#FFFCEF] transition ${
              !file || busy
                ? "bg-[#2C2731]/40 cursor-not-allowed"
                : "bg-[#2C2731] hover:bg-[#241F2A]"
            }`}
          >
            {busy ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
